import { useSetAtom } from 'jotai';
import { erroredOperationAtom, clearSelectionsAtom } from '~/atoms/no-code-atoms';
import { api } from '~/trpc-extra/api';
import type { TestContentMutationContext } from '../callback-handles';
import { cancelTestContentQueries, handleMutationError, handleMutationSuccess } from '../callback-handles';
import { useLiveStateManager } from '../../useLiveStateManager';
import { updateLiveStateOnStepRemoval } from '../cursor-correction';
import { isStepOptimistic } from '../optimistic-handles';

export const useDeleteSteps = ({
  testContentId,
  callbacks,
}: {
  testContentId: string;
  callbacks?: {
    onSuccess?: () => void;
    onError?: () => void;
    onMutate?: () => void;
    onSettled?: () => void;
  };
}) => {
  const trpcUtils = api.useUtils();
  const clearSelections = useSetAtom(clearSelectionsAtom);
  const setErroredOperation = useSetAtom(erroredOperationAtom);
  const { recorderState, updateLiveState } = useLiveStateManager();

  const deleteStepsMutation = api.testStep.deleteSteps.useMutation<TestContentMutationContext>({
    onSettled: () => callbacks?.onSettled?.(),
    onSuccess: async () => {
      await handleMutationSuccess({ trpcUtils, testContentId });
      setErroredOperation(undefined);
      callbacks?.onSuccess?.();
    },
    onError: async (_error, { stepIds }, context) => {
      await handleMutationError({
        trpcUtils,
        testContentId,
        message: `Error unable to delete step${stepIds.length > 1 ? 's' : ''}`,
        context,
      });
      callbacks?.onError?.();
    },
    onMutate: async ({ stepIds }) => {
      await cancelTestContentQueries({ trpcUtils, testContentId });
      const prevData = trpcUtils.testContent.get.getData({ id: testContentId });
      if (!prevData) {
        return { prevData: null };
      }

      clearSelections();

      const stepIdsSet = new Set(stepIds);
      const newData = {
        ...prevData,
        steps: prevData.steps.filter((step) => !stepIdsSet.has(step.id)),
      };

      // If the cursor step is being deleted
      if (recorderState?.stepId && stepIdsSet.has(recorderState.stepId)) {
        const filteredSteps = newData.steps.filter((step) => !isStepOptimistic(step.id));
        updateLiveStateOnStepRemoval({
          steps: filteredSteps,
          recorderState: {
            stepId: recorderState.stepId,
            stepOrder: recorderState.stepOrder,
          },
          updateLiveState,
        });
      }

      trpcUtils.testContent.get.setData({ id: testContentId }, newData);
      callbacks?.onMutate?.();
      return { prevData };
    },
  });

  return deleteStepsMutation;
};
